// External dependencies
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Internal dependencies
import { getFaq } from '@/modules/cms/client/api/cms.api';
import Board from '@/modules/core/client/components/Board';
import LoadingIndicator from '@/modules/core/client/components/LoadingIndicator';

// How many questions a category card previews on the landing page.
const PREVIEW_QUESTIONS = 4;

/**
 * Fragment identifier of a question on its category page.
 *
 * Prefixed with the category slug: anchors are unique per category, not
 * globally, so two categories may share one.
 *
 * @param {Object} category - Category
 * @param {Object} item - Question
 * @returns {String}
 */
function questionId(category, item) {
  return `${category.slug}-${item.anchor}`;
}

/**
 * The public FAQ.
 *
 * Without `category`: the landing page — featured questions, then one card
 * per category. With it: that category's questions, with the other
 * categories in a sidebar.
 *
 * One request serves both: the whole FAQ is small, and the category page
 * needs the full category list for its sidebar anyway.
 *
 * Answers are server sanitized on write and read, which is what makes
 * `dangerouslySetInnerHTML` acceptable here. The JSON-LD schema for crawlers
 * is server-rendered into the page head, not here.
 */
export default function CmsFaq({ category: categorySlug }) {
  const { t, i18n } = useTranslation('cms');

  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    setError(false);

    getFaq({ locale: i18n.language })
      .then(data => {
        if (!cancelled) {
          setCategories((data && data.categories) || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  const category = categorySlug
    ? categories.find(item => item.slug === categorySlug)
    : null;

  useEffect(() => {
    document.title = category
      ? `${category.name} - ${t('FAQ')}`
      : t('Frequently Asked Questions');
  }, [category]);

  // Deep links: once the questions are in the DOM, scroll to the one in the
  // URL hash. The fixed header is accounted for by `scroll-margin-top`.
  useEffect(() => {
    if (isLoading || !window.location.hash) {
      return;
    }
    const target = document.getElementById(
      decodeURIComponent(window.location.hash.slice(1)),
    );
    if (target) {
      target.scrollIntoView();
      target.classList.add('faq-question-flash');
    }
  }, [isLoading, categorySlug]);

  const featured = categories.flatMap(item =>
    item.items
      .filter(question => question.featured)
      .map(question => ({ category: item, question })),
  );

  const header = (
    <Board className="faq-header" names={['happyhippies', 'guitarcamper']}>
      <div className="container">
        <div className="row">
          <div className="col-xs-12 text-center">
            <h2 className="visible-xs-block">{t('FAQ')}</h2>
            <h2 className="hidden-xs">{t('Frequently Asked Questions')}</h2>
            {category && <h3>{category.name}</h3>}
          </div>
        </div>
      </div>
    </Board>
  );

  if (isLoading) {
    return (
      <>
        {header}
        <section className="container container-spacer">
          <LoadingIndicator />
        </section>
      </>
    );
  }

  if (error) {
    return (
      <>
        {header}
        <section className="container container-spacer">
          <div className="alert alert-warning">
            {t('Could not load the FAQ. Please try again later.')}
          </div>
        </section>
      </>
    );
  }

  if (!categories.length) {
    return (
      <>
        {header}
        <section className="container container-spacer">
          <p className="lead">{t('The FAQ is being written.')}</p>
          <p>
            <a href="/support">{t('Contact us')}</a>
          </p>
        </section>
      </>
    );
  }

  // A category URL that does not exist (or has no published questions).
  if (categorySlug && !category) {
    return (
      <>
        {header}
        <section className="container container-spacer">
          <h3>{t('Category not found')}</h3>
          <p>
            <a href="/faq">← {t('All questions')}</a>
          </p>
        </section>
      </>
    );
  }

  if (category) {
    return (
      <>
        {header}
        <section className="container faq-category">
          <div className="row">
            <div className="col-xs-12 col-sm-4 col-md-4">
              <div id="faq-sidebar" className="sidebar">
                <div className="panel panel-default">
                  <div className="panel-heading">
                    <h3 className="panel-title">
                      <a href="/faq" className="undecorated-link text-color">
                        {t('All questions')}
                      </a>
                    </h3>
                  </div>
                  <div className="list-group">
                    {categories.map(item => (
                      <a
                        key={item.slug}
                        href={`/faq/${item.slug}`}
                        className={classnames('list-group-item', {
                          active: item.slug === category.slug,
                        })}
                      >
                        {item.name}
                        <span className="badge">{item.items.length}</span>
                      </a>
                    ))}
                  </div>
                </div>
                <div className="panel panel-default hidden-xs">
                  <div className="panel-heading">
                    <h3 className="panel-title">{category.name}</h3>
                  </div>
                  <div className="list-group">
                    {category.items.map(item => (
                      <a
                        key={item.anchor}
                        href={`#${questionId(category, item)}`}
                        className="list-group-item"
                      >
                        {item.question}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xs-12 col-sm-8 col-md-8">
              {category.description && (
                <p className="lead faq-category-description">
                  {category.description}
                </p>
              )}
              {category.items.map(item => (
                <div
                  key={item.anchor}
                  className="faq-question"
                  id={questionId(category, item)}
                >
                  <h3>
                    <a
                      href={`/faq/${category.slug}#${questionId(
                        category,
                        item,
                      )}`}
                      className="undecorated-link text-color"
                    >
                      {item.question}
                    </a>
                  </h3>
                  <div
                    className="cms-page-body faq-answer"
                    dangerouslySetInnerHTML={{ __html: item.answer }}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      </>
    );
  }

  // Landing page.
  return (
    <>
      {header}
      <section className="container container-spacer faq-landing">
        {featured.length > 0 && (
          <div className="row">
            <div className="col-xs-12">
              <h3 className="faq-landing-heading">{t('Top questions')}</h3>
              <div className="list-group faq-featured">
                {featured.map(({ category: item, question }) => (
                  <a
                    key={questionId(item, question)}
                    href={`/faq/${item.slug}#${questionId(item, question)}`}
                    className="list-group-item"
                  >
                    {question.question}
                    <span className="text-muted faq-featured-category">
                      {item.name}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}

        <h3 className="faq-landing-heading">{t('Browse by topic')}</h3>
        <div className="row faq-categories">
          {categories.map(item => (
            <div key={item.slug} className="col-xs-12 col-sm-6 col-md-4">
              <div className="panel panel-default faq-category-card">
                <div className="panel-heading">
                  <h3 className="panel-title">
                    <a
                      href={`/faq/${item.slug}`}
                      className="undecorated-link text-color"
                    >
                      {item.name}
                    </a>
                  </h3>
                </div>
                <div className="panel-body">
                  {item.description && (
                    <p className="text-muted">{item.description}</p>
                  )}
                  <ul className="list-unstyled">
                    {item.items.slice(0, PREVIEW_QUESTIONS).map(question => (
                      <li key={question.anchor}>
                        <a
                          href={`/faq/${item.slug}#${questionId(
                            item,
                            question,
                          )}`}
                        >
                          {question.question}
                        </a>
                      </li>
                    ))}
                  </ul>
                  <a href={`/faq/${item.slug}`} className="faq-category-more">
                    {t('All {{count}} questions', {
                      count: item.items.length,
                    })}{' '}
                    →
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="faq-landing-support">
          {t("Didn't find an answer?")} <a href="/support">{t('Contact us')}</a>
        </p>
      </section>
    </>
  );
}

CmsFaq.propTypes = {
  category: PropTypes.string,
};
